import { createApp, nextTick } from 'vue';
import store from '../store';

/**
 * One island registry. Each custom element is loaded only when it is in the
 * document. The package #app tree is not compiled or mounted.
 *
 * `needs` lists the shared runtime that island actually uses. Vue, Vuex and
 * Vuetify stay in shared chunks instead of being copied into every island.
 */
export const ISLAND_DEFINITIONS = [
    {
        selector: 'shopping-cart',
        aboveFold: true,
        needs: { store: true },
        geometry: 'cart',
        load: () => import(/* webpackChunkName: 'shopping-cart' */ '../components/ShoppingCart.vue'),
    },
    {
        selector: 'buy-vignette',
        aboveFold: true,
        needs: { store: true, router: true, vuetify: true, svg: true },
        load: () => import(/* webpackChunkName: 'buy-vignette' */ '../components/BuyVignette/BuyVignette.vue'),
    },
    {
        selector: 'payment-processing',
        aboveFold: true,
        needs: { store: true, router: true, svg: true },
        load: () => import(/* webpackChunkName: 'payment-processing' */ '../components/PaymentProcessing.vue'),
    },
    {
        selector: 'validity-check',
        aboveFold: true,
        needs: { store: true, svg: true },
        load: () => import(/* webpackChunkName: 'validity-check' */ '../components/ValidityCheck.vue'),
    },
    {
        selector: 'select-app-currency',
        aboveFold: true,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'select-app-currency' */ '../components/inputs/SelectAppCurrency.vue'),
    },
    {
        selector: 'header-language-selector',
        aboveFold: true,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'header-language-selector' */ '../components/HeaderLanguageSelector.vue'),
    },
    {
        selector: 'mobile-header',
        aboveFold: true,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'mobile-header' */ '../components/MobileHeader.vue'),
    },
    {
        selector: 'pricing-section',
        aboveFold: false,
        needs: { store: true, svg: true },
        load: () => import(/* webpackChunkName: 'pricing-section' */ '../components/PricingSection.vue'),
    },
    {
        selector: 'vignette-prices',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'vignette-prices' */ '../components/VignettePrices.vue'),
    },
    {
        selector: 'language-selector-block',
        aboveFold: false,
        needs: { store: true, svg: true },
        load: () => import(/* webpackChunkName: 'language-selector-block' */ '../components/LanguageSelectorBlock.vue'),
    },
    {
        selector: 'home-license-plate-search',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'home-license-plate-search' */ '../components/HomeLicensePlateSearch.vue'),
    },
    {
        selector: 'contact-languages',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'contact-languages' */ '../components/ContactLanguages.vue'),
    },
    {
        selector: 'payment-methods-pills',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'payment-methods-pills' */ '../components/PaymentMethodsPills.vue'),
    },
    {
        selector: 'payment-methods-country-select',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'payment-methods-country-select' */ '../components/PaymentMethodsCountrySelect.vue'),
    },
    {
        selector: 'payment-methods-grid',
        aboveFold: false,
        needs: {},
        load: () => import(/* webpackChunkName: 'payment-methods-grid' */ '../components/PaymentMethodsGrid.vue'),
    },
    {
        selector: 'europe-map',
        aboveFold: false,
        needs: { svg: true },
        load: () => import(/* webpackChunkName: 'europe-map' */ '../components/EuropeMap.vue'),
    },
    {
        selector: 'why-choose-us-language-dropdown',
        aboveFold: false,
        needs: { store: true, svg: true },
        load: () => import(/* webpackChunkName: 'why-choose-us-language' */ '../components/WhyChooseUsLanguageDropdown.vue'),
    },
    {
        selector: 'team-4-flags',
        aboveFold: false,
        needs: {},
        load: () => import(/* webpackChunkName: 'team-4-flags' */ '../components/Team4Flags.vue'),
    },
];

const SKIP_DATA_ATTRIBUTES = new Set([
    'data-vue-ready',
    'data-client-ready',
    'data-client-rendered',
    'data-geometry-ready',
    'data-ssr-fallback',
    'data-mount-when-near',
]);

const NEAR_VIEWPORT_MARGIN = '600px 0px';
const GEOMETRY_TOLERANCE_PX = 8;

let routerPromise = null;

function loadRouter() {
    if (!routerPromise) {
        routerPromise = import(/* webpackChunkName: 'router' */ '../router.js');
    }
    return routerPromise;
}

function kebabToCamel(value) {
    return value.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}

function parseDataValue(raw) {
    if (raw == null) return undefined;
    const trimmed = String(raw).trim();
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (trimmed === 'null') return null;
    if (trimmed === '') return '';
    const first = trimmed[0];
    if (first === '{' || first === '[' || first === '"') {
        try {
            return JSON.parse(trimmed);
        } catch (e) {
            return raw;
        }
    }
    return raw;
}

export function readIslandProps(el) {
    const props = {};
    for (const attr of Array.from(el.attributes)) {
        if (!attr.name.startsWith('data-') || SKIP_DATA_ATTRIBUTES.has(attr.name)) continue;
        const key = kebabToCamel(attr.name.slice(5));
        if (!key) continue;
        props[key] = parseDataValue(attr.value);
    }
    if (el.matches('pricing-section')) {
        Object.assign(props, pricingProps(el));
    }
    return props;
}

function pricingProps(el) {
    const host = el.closest('#pricing-app');
    if (!host) return {};
    return {
        variant: host.dataset.variant || null,
        forceEuro: host.dataset.forceEuro === '1',
        ctaTextKey: host.dataset.ctaTextKey || null,
        sectionObjects: parseDataValue(host.dataset.sectionObjects) || {},
        sectionTexts: parseDataValue(host.dataset.sectionTexts) || {},
        standardPriceFromBlade: host.dataset.standardPrice || '',
    };
}

function markIslandReady(el) {
    el.setAttribute('data-client-rendered', 'true');
    const island = el.closest('.vue-island');
    if (island) island.setAttribute('data-vue-ready', 'true');
}

function exposeCartWhenGeometryMatches(el) {
    const island = el.closest('.vue-island');
    const ssr = island?.querySelector('.shopping-cart-html');
    const vueRoot = el.firstElementChild;
    if (!island || !ssr || !vueRoot) return;

    const previous = el.getAttribute('style');
    el.style.position = 'absolute';
    el.style.inset = '0';
    el.style.width = '100%';
    el.style.height = 'auto';
    el.style.visibility = 'hidden';
    el.style.pointerEvents = 'none';
    el.style.overflow = 'visible';
    el.style.clip = 'auto';
    el.style.margin = '0';

    const ssrRect = ssr.getBoundingClientRect();
    const vueRect = vueRoot.getBoundingClientRect();
    const widthOk = Math.abs(vueRect.width - ssrRect.width) <= GEOMETRY_TOLERANCE_PX;
    const heightOk = Math.abs(vueRect.height - ssrRect.height) <= GEOMETRY_TOLERANCE_PX;

    if (previous == null) el.removeAttribute('style');
    else el.setAttribute('style', previous);

    if (!widthOk || !heightOk) return;

    el.setAttribute('data-geometry-ready', 'true');
    markIslandReady(el);
}

function mountElement(el, definition) {
    if (!el || el.__vsMounting || el.__vue_app__) return;
    el.__vsMounting = true;

    const props = readIslandProps(el);
    definition.load()
        .then(async ({ default: Component }) => {
            if (!el.isConnected || el.__vue_app__) return;
            const app = createApp(Component, props);
            if (definition.needs.store) app.use(store);
            if (definition.needs.router) {
                const routerModule = await loadRouter();
                app.use(routerModule.default);
            }
            if (definition.needs.vuetify) {
                const vuetifyModule = await import(/* webpackChunkName: 'vuetify-boot' */ '../vuetifyDatePicker.js');
                const langCode = typeof window !== 'undefined' ? window.__APP_DATA__?.lang?.code : null;
                app.use(vuetifyModule.createAppVuetify(langCode));
            }
            if (definition.needs.svg) {
                const svgModule = await import(/* webpackChunkName: 'svg-icon' */ '../components/SvgIcon.vue');
                app.component('SvgIcon', svgModule.default);
                app.component('svg-icon', svgModule.default);
            }
            app.mount(el);
            await nextTick();
            if (!el.isConnected) return;
            if (definition.geometry === 'cart') {
                exposeCartWhenGeometryMatches(el);
                return;
            }
            markIslandReady(el);
        })
        .catch((err) => {
            el.__vsMounting = false;
            if (typeof console !== 'undefined' && console.error) {
                console.error(`[island] ${definition.selector} failed`, err);
            }
        });
}

function isImmediateIsland(definition, el) {
    if (!definition.aboveFold) return false;
    if (definition.selector === 'select-app-currency') {
        return Boolean(el.closest('header'));
    }
    return true;
}

function scheduleWhenNear(el, definition) {
    const mount = () => mountElement(el, definition);
    const details = el.closest('details');
    if (details && !details.open) {
        details.addEventListener('toggle', function onToggle() {
            if (!details.open) return;
            details.removeEventListener('toggle', onToggle);
            mount();
        });
    }
    if (typeof IntersectionObserver !== 'function') {
        mount();
        return;
    }
    const host = el.closest('.vue-island') || el;
    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        mount();
    }, { root: null, rootMargin: NEAR_VIEWPORT_MARGIN, threshold: 0 });
    observer.observe(host);
}

export function mountRegisteredIslands() {
    if (typeof document === 'undefined') return [];
    const scheduled = [];

    for (const definition of ISLAND_DEFINITIONS) {
        const nodes = document.querySelectorAll(definition.selector);
        nodes.forEach((el) => {
            if (el.__cvIslandScheduled) return;
            el.__cvIslandScheduled = true;
            scheduled.push(definition.selector);
            if (isImmediateIsland(definition, el)) {
                mountElement(el, definition);
                return;
            }
            scheduleWhenNear(el, definition);
        });
    }

    return scheduled;
}
